import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from './SeoMeta';
import {
  TrendingUp,
  Users,
  MessageSquare,
  CheckCircle,
  ArrowRight,
  BarChart3,
  Shield,
  Zap,
  ChevronDown,
  ChevronUp,
  Sparkles,
  FlaskConical,
  ExternalLink,
} from 'lucide-react';

/* ─── Inline LinkedIn SVG so we don't pull in an extra dependency ─── */
/* ─── FAQ item with expand/collapse ─── */
const FAQItem: React.FC<{ q: string; a: string }> = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-200 last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left group"
      >
        <span className="text-sm font-medium text-slate-900 group-hover:text-navy-500 transition-colors pr-4">{q}</span>
        {open
          ? <ChevronUp className="w-4 h-4 text-navy-500 shrink-0" />
          : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
      </button>
      {open && (
        <p className="text-sm text-slate-500 leading-relaxed pb-5">{a}</p>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════
   Main Component
══════════════════════════════════════════════════════════════════════ */
const HomePage: React.FC = () => {
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "IndianStockMarketGPT",
    "url": "https://indianstockmarketgpt.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://indianstockmarketgpt.com/blog?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "IndianStockMarketGPT",
    "url": "https://indianstockmarketgpt.com",
    "logo": "https://indianstockmarketgpt.com/favicon-192x192.svg",
    "description": "Free AI-powered stock market research and education platform for Indian retail investors",
    "founder": { "@type": "Person", "name": "Shantanu Bose" },
    "foundingDate": "2024",
    "sameAs": []
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "What is IndianStockMarketGPT?", "acceptedAnswer": { "@type": "Answer", "text": "IndianStockMarketGPT is a free AI-powered stock market research and education platform built specifically for Indian retail investors. It analyses 1000+ market indicators per query and delivers plain-English research on any NSE or BSE-listed stock in under 90 seconds." } },
      { "@type": "Question", "name": "Is IndianStockMarketGPT free to use?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, IndianStockMarketGPT offers free lifetime access to AI-powered educational stock analysis. A Pro plan is available for advanced features." } },
      { "@type": "Question", "name": "Does IndianStockMarketGPT provide investment advice?", "acceptedAnswer": { "@type": "Answer", "text": "No. IndianStockMarketGPT is an educational research tool, not a SEBI-registered investment adviser. All outputs are informational and for educational purposes only. Always consult a qualified financial adviser before investing." } },
      { "@type": "Question", "name": "Which stocks can I analyse with IndianStockMarketGPT?", "acceptedAnswer": { "@type": "Answer", "text": "You can analyse any stock listed on NSE or BSE — including Nifty 50, Bank Nifty, Sensex components, mid-caps, small-caps, and sector indices like IT, Pharma, FMCG, and Auto." } },
      { "@type": "Question", "name": "How does IndianStockMarketGPT analyse stocks?", "acceptedAnswer": { "@type": "Answer", "text": "Our AI processes 1000+ market indicators simultaneously — technical signals (RSI, MACD, Bollinger Bands), fundamental data (P/E, ROE, debt ratios), sector trends, and 10+ years of historical patterns — and synthesises them into plain-English educational analysis in under 90 seconds." } },
      { "@type": "Question", "name": "How is IndianStockMarketGPT different from Screener.in or TradeBrains?", "acceptedAnswer": { "@type": "Answer", "text": "Screener.in and TradeBrains show you the data. IndianStockMarketGPT synthesises it into plain-English analysis so you understand what the data means without needing a finance degree. Think of it as an AI research analyst available 24/7." } }
    ]
  };

  return (
    <>
      <SeoMeta
        title="IndianStockMarketGPT — Free AI Stock Analysis for NSE & BSE"
        description="Get free AI-powered educational analysis for any NSE or BSE stock in under 90 seconds. 1000+ indicators. Plain English. No jargon. No sign-up required."
        keywords="AI stock analysis India, Indian stock market AI, NSE BSE analysis, Nifty AI analysis, Bank Nifty AI, free stock research India, AI stock market tool India"
        url="https://indianstockmarketgpt.com/"
        type="website"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <div className="min-h-screen bg-white text-slate-900">

        {/* ════════ HERO ════════ */}
        <section className="bg-white pt-16">

          {/* Top trust bar */}
          <div className="bg-navy-950 py-2.5">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-6">
                <span className="text-xs text-slate-300 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block" />
                  NSE & BSE markets live
                </span>
                <span className="text-xs text-slate-400 hidden sm:block">Free AI research · No sign-up required</span>
              </div>
              <span className="text-xs text-slate-400">Educational use only · Not investment advice</span>
            </div>
          </div>

          {/* Hero content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

              {/* Left: Copy */}
              <div>
                {/* Eyebrow */}
                <div className="flex items-center gap-2 mb-6">
                  <span className="inline-flex items-center gap-2 text-xs font-semibold text-navy-500 bg-navy-50 border border-navy-100 px-3 py-1.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-navy-500" />
                    25,000+ investors trust ISMGPT
                  </span>
                </div>

                {/* Headline */}
                <h1 className="font-serif text-slate-900 leading-tight mb-6" style={{ fontSize: 'clamp(2.4rem, 5vw, 3.75rem)' }}>
                  Research any Indian<br />
                  stock like a{' '}
                  <span className="italic text-navy-500">professional.</span>
                </h1>

                {/* Sub-copy */}
                <p className="text-lg text-slate-500 leading-relaxed mb-8 max-w-lg">
                  AI-powered analysis of 1,000+ indicators — technicals, fundamentals, sector context — delivered as a plain-English research brief in under 90 seconds.
                </p>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-3 mb-8">
                  <a href="https://chatgpt.com/g/g-jkxrUFNja-indian-stock-market"
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-navy-500 hover:bg-navy-600 px-7 py-4 rounded-xl transition-colors shadow-sm">
                    Analyse a stock free
                    <ArrowRight size={16} />
                  </a>
                  <Link to="/blog"
                    className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-navy-600 bg-white hover:bg-navy-50 border border-navy-200 px-7 py-4 rounded-xl transition-colors">
                    Read research →
                  </Link>
                </div>

                {/* Trust signals */}
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <CheckCircle size={13} className="text-green-500 shrink-0" />
                  Free forever &nbsp;·&nbsp;
                  <CheckCircle size={13} className="text-green-500 shrink-0" />
                  No credit card &nbsp;·&nbsp;
                  <CheckCircle size={13} className="text-green-500 shrink-0" />
                  NSE &amp; BSE stocks
                </div>
              </div>

              {/* Right: Stats + indicators panel */}
              <div className="bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                {/* Panel header */}
                <div className="bg-navy-950 px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-white font-semibold text-sm">NIFTY 50</span>
                    <span className="font-mono text-white text-sm">24,198.85</span>
                    <span className="font-mono text-green-400 text-xs font-semibold">+0.43%</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                    Live
                  </div>
                </div>

                {/* Sample analysis output */}
                <div className="p-6 space-y-4">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Sample AI Analysis</p>

                  {/* Stock header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-slate-900 text-base">Reliance Industries</p>
                      <p className="text-xs text-slate-400 font-mono">NSE: RELIANCE · ₹2,891.45</p>
                    </div>
                    <span className="text-xs font-semibold text-green-600 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">Bullish signal</span>
                  </div>

                  <div className="h-px bg-slate-200" />

                  {/* Indicator rows */}
                  {[
                    { label: 'RSI (14)', value: '58.4', note: 'Neutral zone — no overbought risk', color: 'text-slate-700' },
                    { label: 'MACD', value: 'Bullish cross', note: 'Signal crossed above baseline', color: 'text-green-600' },
                    { label: 'EMA 50 / 200', value: 'Aligned bullish', note: 'Price above both moving averages', color: 'text-green-600' },
                    { label: 'P/E Ratio', value: '24.1x', note: 'In-line with sector average', color: 'text-slate-700' },
                  ].map(({ label, value, note, color }) => (
                    <div key={label} className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-500">{label}</p>
                        <p className="text-xs text-slate-400 leading-snug mt-0.5">{note}</p>
                      </div>
                      <span className={`font-mono text-xs font-semibold shrink-0 ${color}`}>{value}</span>
                    </div>
                  ))}

                  <div className="h-px bg-slate-200" />

                  <p className="text-[11px] text-slate-400 leading-relaxed">
                    ⚠ This is an illustrative example. ISMGPT provides educational analysis only and does not constitute investment advice.
                  </p>
                </div>
              </div>

            </div>
          </div>

          {/* Stats strip */}
          <div className="border-t border-slate-100 bg-slate-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: '< 90s', label: 'Analysis time' },
                { value: '1,000+', label: 'Indicators per query' },
                { value: '25,000+', label: 'Active investors' },
                { value: '100% Free', label: 'No subscription' },
              ].map(({ value, label }) => (
                <div key={label} className="text-center">
                  <p className="font-semibold text-navy-600 text-xl mb-0.5">{value}</p>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">{label}</p>
                </div>
              ))}
            </div>
          </div>

        </section>

        {/* ════════ RESEARCH ════════ */}
        <section className="bg-white py-20 px-4 sm:px-6 lg:px-8 border-t border-slate-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="text-xs font-semibold text-navy-500 uppercase tracking-widest mb-2">Research & Insights</p>
                <h2 className="font-serif text-2xl text-slate-900">Latest from our analysts</h2>
              </div>
              <Link to="/blog" className="text-sm font-medium text-navy-500 hover:text-navy-700 transition-colors hidden sm:block">
                View all research →
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  tag: 'Technical Analysis',
                  title: 'Nifty 50 breaks key resistance — what RSI and MACD are signalling now',
                  excerpt: 'After weeks of consolidation, Nifty 50 breached the 24,000 mark. Here is what three key indicators suggest about the next move.',
                  date: 'May 2026',
                  read: '4 min read',
                },
                {
                  tag: 'Fundamental',
                  title: 'HDFC Bank Q4 results decoded: ROE, NIM, and the analyst view',
                  excerpt: 'Q4 results surprised on NIM. We break down what the numbers mean for retail investors and whether the valuation makes sense at current levels.',
                  date: 'May 2026',
                  read: '6 min read',
                },
                {
                  tag: 'Market Overview',
                  title: 'India VIX at 13-year low — why low volatility can be the hidden risk',
                  excerpt: 'Low VIX is typically bullish, but history shows it can precede sharp corrections. We look at what this means for your portfolio positioning.',
                  date: 'May 2026',
                  read: '3 min read',
                },
              ].map(({ tag, title, excerpt, date, read }) => (
                <Link key={title} to="/blog"
                  className="group bg-white border border-slate-200 hover:border-navy-200 hover:shadow-md rounded-xl p-6 flex flex-col gap-4 transition-all">
                  <span className="text-xs font-semibold text-navy-500 bg-navy-50 border border-navy-100 px-2.5 py-1 rounded-full w-fit">{tag}</span>
                  <h3 className="font-serif text-slate-900 text-base leading-snug group-hover:text-navy-600 transition-colors">{title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed flex-1">{excerpt}</p>
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <span className="text-xs text-slate-400">{date} · {read}</span>
                    <ArrowRight size={14} className="text-slate-300 group-hover:text-navy-500 group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-8 sm:hidden">
              <Link to="/blog" className="text-sm font-medium text-navy-500 hover:text-navy-700">View all research →</Link>
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════════════════
            BETA FEATURE TEASER — /ask
        ════════════════════════════════════════════════════════════ */}
        <section className="py-10 px-4 sm:px-6 lg:px-8 bg-slate-50 border-t border-slate-100">
          <div className="max-w-5xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm">
              <div className="relative flex flex-col lg:flex-row items-center lg:items-stretch gap-0">

                {/* Left — copy */}
                <div className="flex-1 p-8 md:p-10">
                  {/* Badges row */}
                  <div className="flex items-center gap-2.5 mb-5 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 bg-navy-50 border border-navy-100 text-navy-500 text-[10px] font-semibold tracking-widest uppercase px-2.5 py-1 rounded-full">
                      <FlaskConical className="w-3 h-3" />
                      Beta
                    </span>
                    <span className="inline-flex items-center gap-1.5 bg-slate-100 border border-slate-200 text-slate-500 text-[10px] font-medium tracking-wide uppercase px-2.5 py-1 rounded-full">
                      <Sparkles className="w-3 h-3 text-gold-400" />
                      New feature
                    </span>
                  </div>

                  <h2 className="text-xl md:text-2xl font-serif text-slate-900 mb-3 leading-snug">
                    Ask anything about any Indian stock —{' '}
                    <span className="italic text-navy-500">right here on the site.</span>
                  </h2>

                  <p className="text-sm text-slate-500 leading-relaxed mb-6 max-w-lg">
                    We're building a native AI research experience: live price charts, key fundamentals,
                    and AI-generated analysis — all without leaving IndianStockMarketGPT.
                    It's early, it's rough around the edges, and we'd love your feedback.
                  </p>

                  {/* Feature pills */}
                  <div className="flex flex-wrap gap-2 mb-8">
                    {['Live price chart', 'Fundamentals panel', 'AI analysis', 'NSE & BSE'].map(f => (
                      <span key={f} className="text-xs text-slate-500 bg-slate-100 border border-slate-200 px-3 py-1 rounded-full">
                        {f}
                      </span>
                    ))}
                  </div>

                  {/* CTAs */}
                  <div className="flex flex-wrap items-center gap-3">
                    <Link
                      to="/ask"
                      className="inline-flex items-center gap-2 bg-navy-500 hover:bg-navy-600 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors text-sm"
                    >
                      Try the beta
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                    <a
                      href="https://tinyurl.com/stockmarketgpt"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors border border-slate-200 hover:border-slate-300 px-5 py-2.5 rounded-lg"
                    >
                      Share feedback
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>

                {/* Right — mock preview card */}
                <div className="lg:w-72 xl:w-80 shrink-0 border-t lg:border-t-0 lg:border-l border-slate-200 bg-slate-50 p-6 md:p-8 flex flex-col justify-center gap-4">
                  {/* Mini chart mock */}
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-xs font-semibold text-slate-900">RELIANCE</p>
                        <p className="text-[10px] text-slate-400">NSE · Equity</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-slate-900 font-mono">₹2,947</p>
                        <p className="text-[10px] text-green-600">+1.4%</p>
                      </div>
                    </div>
                    {/* SVG sparkline */}
                    <svg viewBox="0 0 120 36" className="w-full h-8" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#1B4FD8" stopOpacity="0.2" />
                          <stop offset="100%" stopColor="#1B4FD8" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path d="M0 28 C15 26,20 22,30 20 C40 18,45 24,55 18 C65 12,70 16,80 10 C90 4,100 8,120 6" fill="none" stroke="#1B4FD8" strokeWidth="1.5" />
                      <path d="M0 28 C15 26,20 22,30 20 C40 18,45 24,55 18 C65 12,70 16,80 10 C90 4,100 8,120 6 L120 36 L0 36Z" fill="url(#chartGrad)" />
                    </svg>
                  </div>

                  {/* Metric pills */}
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'P/E Ratio', value: '28.4' },
                      { label: 'RSI (14)', value: '58' },
                      { label: 'Market Cap', value: '₹19.9T' },
                      { label: 'ROE', value: '9.8%' },
                    ].map(({ label, value }) => (
                      <div key={label} className="bg-white rounded-lg border border-slate-200 px-3 py-2">
                        <p className="text-[10px] text-slate-400 mb-0.5">{label}</p>
                        <p className="text-xs font-semibold text-slate-900 font-mono">{value}</p>
                      </div>
                    ))}
                  </div>

                  <p className="text-[10px] text-slate-400 text-center leading-snug">
                    Illustrative preview · Live data in beta
                  </p>
                </div>

              </div>
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════════════════
            HOW IT WORKS
        ════════════════════════════════════════════════════════════ */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white border-t border-slate-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-serif text-slate-900 mb-14 text-center">
              Research any stock in three steps
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {[
                {
                  n: '01',
                  title: 'Ask your question',
                  desc: 'Type any question about an NSE or BSE stock — technicals, fundamentals, sector outlook, or a specific pattern you want explained.'
                },
                {
                  n: '02',
                  title: 'AI analyses 1,000+ signals',
                  desc: 'Our model processes technical indicators (RSI, MACD, EMA), fundamentals (P/E, ROE, debt), sector trends, and 10+ years of historical data — simultaneously.'
                },
                {
                  n: '03',
                  title: 'Get plain-English insights',
                  desc: 'In under 90 seconds you receive a clear, jargon-free research summary — no charts to decode, no data to interpret yourself.'
                },
              ].map(({ n, title, desc }) => (
                <div key={n} className="relative">
                  <p className="text-5xl font-serif text-navy-100 mb-4 leading-none">{n}</p>
                  <h3 className="text-base font-semibold text-slate-900 mb-2">{title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════════════════
            CAPABILITIES
        ════════════════════════════════════════════════════════════ */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50 border-y border-slate-200">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              {/* Left: heading + intro */}
              <div className="lg:sticky lg:top-24">
                <h2 className="text-2xl md:text-3xl font-serif text-slate-900 mb-5">
                  Everything a retail investor needs to research smarter
                </h2>
                <p className="text-sm text-slate-500 leading-relaxed mb-8">
                  Built specifically for Indian markets — not a global tool retrofitted for NSE and BSE.
                  Every feature is designed around how Indian retail investors actually research and trade.
                </p>
                <a
                  href="https://chatgpt.com/g/g-jkxrUFNja-indian-stock-market"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-navy-500 hover:bg-navy-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors inline-flex items-center gap-2 text-sm"
                >
                  Try it free
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
              {/* Right: capability list */}
              <div className="space-y-0 divide-y divide-slate-200">
                {[
                  {
                    icon: <TrendingUp className="w-5 h-5" />,
                    title: 'Nifty & Bank Nifty analysis',
                    desc: 'Daily AI-synthesised outlook for index movements — technical patterns, momentum, volume trends, and derivative signals in plain English.'
                  },
                  {
                    icon: <BarChart3 className="w-5 h-5" />,
                    title: 'Deep technical analysis',
                    desc: 'RSI, MACD, Bollinger Bands, EMA crossovers, support/resistance levels, and candlestick patterns — analysed and explained without charts.'
                  },
                  {
                    icon: <Shield className="w-5 h-5" />,
                    title: 'Risk management guidance',
                    desc: 'Position sizing principles, stop-loss logic, and capital protection strategies explained for any stock you\'re researching.'
                  },
                  {
                    icon: <Zap className="w-5 h-5" />,
                    title: 'Market context alerts',
                    desc: 'Premium Telegram alerts for breakouts, key levels, and market-moving events — delivered as educational context for your own research.'
                  },
                  {
                    icon: <Users className="w-5 h-5" />,
                    title: 'Plain-English fundamentals',
                    desc: 'P/E ratio, ROE, debt-to-equity, promoter holding, and quarterly results — explained in language that doesn\'t require a CFA.'
                  },
                  {
                    icon: <MessageSquare className="w-5 h-5" />,
                    title: 'Sector & macro context',
                    desc: 'FII/DII flows, sector rotation, RBI policy impact, and global cues — the bigger picture behind any stock move.'
                  },
                ].map(({ icon, title, desc }) => (
                  <div key={title} className="flex gap-4 py-5 group">
                    <div className="text-navy-500 shrink-0 mt-0.5">{icon}</div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900 mb-1">{title}</h3>
                      <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════════════════
            PRICING
        ════════════════════════════════════════════════════════════ */}
        <section id="pricing" className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50 border-y border-slate-200">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-serif text-slate-900 mb-3 text-center">Simple, honest pricing</h2>
            <p className="text-sm text-slate-500 text-center mb-14">The core platform is free forever. No credit card.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Free */}
              <div className="bg-white rounded-2xl p-8 border border-slate-200">
                <p className="text-xs text-slate-400 tracking-widest uppercase mb-4">Free · Lifetime</p>
                <p className="text-4xl font-serif text-slate-900 mb-6">₹0</p>
                <ul className="space-y-3 mb-8">
                  {[
                    'Full access to Indian Stock Market GPT',
                    'Daily Nifty & Bank Nifty analysis',
                    'Unlimited questions — no monthly cap',
                    'Educational stock research for any NSE/BSE stock',
                    'No sign-up required to start',
                  ].map(item => (
                    <li key={item} className="flex items-start gap-3 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
                <a
                  href="https://chatgpt.com/g/g-jkxrUFNja-indian-stock-market"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full block text-center bg-navy-500 hover:bg-navy-600 text-white font-semibold py-3 rounded-lg transition-colors text-sm"
                >
                  Start for free
                </a>
              </div>

              {/* Pro */}
              <div className="bg-white rounded-2xl p-8 border border-slate-200 relative">
                <div className="absolute top-0 right-6 -translate-y-1/2 bg-navy-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  Coming soon
                </div>
                <p className="text-xs text-slate-400 tracking-widest uppercase mb-4">Pro · Monthly</p>
                <p className="text-4xl font-serif text-navy-500 mb-6">TBA</p>
                <ul className="space-y-3 mb-8">
                  {[
                    'Everything in Free',
                    'Premium Telegram alerts',
                    'Priority support access',
                    'Advanced portfolio analysis',
                    'Risk management tools',
                    'Elite Wealth Managers List',
                  ].map(item => (
                    <li key={item} className="flex items-start gap-3 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
                <button
                  disabled
                  className="w-full block text-center bg-slate-100 text-slate-400 font-semibold py-3 rounded-lg cursor-not-allowed text-sm border border-slate-200"
                >
                  Join waitlist
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════════════════
            FAQ
        ════════════════════════════════════════════════════════════ */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white" id="faq">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-serif text-slate-900 mb-12 text-center">
              Common questions
            </h2>
            <div className="bg-slate-50 rounded-2xl border border-slate-200 px-6">
              {[
                { q: 'How does IndianStockMarketGPT analyse stocks?', a: 'The AI processes 1,000+ market indicators simultaneously — technical signals, fundamental data, sector trends, and 10+ years of historical patterns — and synthesises them into plain-English educational analysis in under 90 seconds. This is for research and education only, not investment advice.' },
                { q: 'Can I use it alongside my existing broker?', a: 'Yes. IndianStockMarketGPT works as a research layer on top of any broker — Zerodha, Groww, Upstox, Angel One, or any other. You keep your existing account and use our AI to make more informed decisions.' },
                { q: 'What makes this different from Screener.in or TradeBrains?', a: 'Screener.in and TradeBrains show you the data. IndianStockMarketGPT synthesises it into plain-English analysis so you understand what the data means without needing a finance degree. Think of it as an AI research analyst available 24/7.' },
                { q: 'Is it really free forever?', a: 'Yes. The core AI research platform is free for life. We may offer premium features separately, but fundamental analysis will always remain free.' },
                { q: 'Does it provide investment advice?', a: 'No. IndianStockMarketGPT is an educational research tool, not a SEBI-registered investment adviser. All outputs are for educational purposes only. Always consult a qualified financial adviser before investing.' },
                { q: 'Which stocks can I research?', a: 'Any stock listed on NSE or BSE — Nifty 50, Bank Nifty, Sensex components, mid-caps, small-caps, and sector indices like IT, Pharma, FMCG, and Auto.' },
              ].map(item => <FAQItem key={item.q} q={item.q} a={item.a} />)}
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════════════════
            FINAL CTA
        ════════════════════════════════════════════════════════════ */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50 border-t border-slate-200">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-10 h-10 border border-navy-100 bg-navy-50 rounded-xl flex items-center justify-center mx-auto mb-7">
              <TrendingUp className="w-5 h-5 text-navy-500" />
            </div>
            <h2 className="text-2xl md:text-4xl font-serif text-slate-900 mb-4">
              Start researching smarter today.
            </h2>
            <p className="text-sm text-slate-500 mb-9 max-w-md mx-auto leading-relaxed">
              Free forever. No sign-up needed. Join 25,000+ investors who already use AI to understand the Indian market.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/ask"
                className="bg-navy-500 hover:bg-navy-600 text-white font-semibold px-8 py-3.5 rounded-lg transition-colors text-sm inline-flex items-center gap-2 justify-center"
              >
                Analyse a stock now
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://tinyurl.com/stockmarketgpt"
                target="_blank"
                rel="noopener noreferrer"
                className="border border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-300 px-8 py-3.5 rounded-lg text-sm font-medium transition-all"
              >
                Join the community
              </a>
            </div>

            {/* Affiliate broker strip */}
            <div className="mt-10 pt-8 border-t border-slate-200">
              <p className="text-xs text-slate-400 mb-4">Don't have a demat account yet? Open one free:</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <a href="https://zerodha.com/open-account?c=JA0171" target="_blank" rel="noopener noreferrer sponsored"
                  className="flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 text-slate-600 hover:text-slate-900 px-4 py-2 rounded-lg text-xs font-medium transition-all">
                  <span className="w-4 h-4 rounded bg-blue-600 flex items-center justify-center text-[9px] font-bold text-white">Z</span>
                  Zerodha
                </a>
                <a href="https://upstox.onelink.me/0H1s/KY1170" target="_blank" rel="noopener noreferrer sponsored"
                  className="flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 text-slate-600 hover:text-slate-900 px-4 py-2 rounded-lg text-xs font-medium transition-all">
                  <span className="w-4 h-4 rounded bg-purple-600 flex items-center justify-center text-[9px] font-bold text-white">U</span>
                  Upstox
                </a>
                <a href="https://angel-one.onelink.me/Wjgr/ahr63txg" target="_blank" rel="noopener noreferrer sponsored"
                  className="flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 text-slate-600 hover:text-slate-900 px-4 py-2 rounded-lg text-xs font-medium transition-all">
                  <span className="w-4 h-4 rounded bg-orange-600 flex items-center justify-center text-[9px] font-bold text-white">A</span>
                  Angel One
                </a>
                <Link to="/best-demat-account-india"
                  className="flex items-center gap-1 text-xs text-navy-500 hover:text-navy-700 px-3 py-2 transition-colors">
                  Compare all brokers →
                </Link>
              </div>
              <p className="text-[10px] text-slate-400 mt-3">Affiliate links — we earn a small commission at no cost to you.</p>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default HomePage;
